import React, {AllHTMLAttributes, useContext, useMemo} from 'react';
import {ListViewItemContext} from './list-view-item-context';
import {ContainingListViewIdAttributeName, ListViewItemIdAttributeName} from './list-view-dom';
import {classes, style} from './list-view-item-root.st.css';
import {ListViewRole, StateUpdateReason} from './list-view-types';
import {Button} from "../button/button";
import {evaluateFunction} from "@wix/devzai-utils-common";

export enum ListViewItemInteractionTriggeringEvent {
    None,
    Click,
    MouseDown,
    LeftButtonClick,
    LeftButtonDown
}

export const ListViewItemRoot = React.memo(function ListViewItemRoot (props: ListViewItemRoot.Props) {


    const renderProps = useContext(ListViewItemContext);
    const listViewItemRootAttributes = useListViewItemRootAttributes();

    const {
        tagName = 'div',
        interactionTriggeringEvent = ListViewItemInteractionTriggeringEvent.MouseDown,
        elementRef,
        renderRootElement = defaultRenderRootElement,
        ...restCommonProps
    } = props;

    if (renderProps === null) {
        return renderRootElement(tagName, restCommonProps);
    } else {
        const {
            onClick: originalOnClick,
            onMouseDown: originalOnMouseDown,
            className,
            ...restProps
        } = restCommonProps;

        const overridenProps = {
            onClick: originalOnClick,
            onMouseDown: originalOnMouseDown
        };

        switch (interactionTriggeringEvent) {
            case ListViewItemInteractionTriggeringEvent.MouseDown:
            case ListViewItemInteractionTriggeringEvent.LeftButtonDown:
                overridenProps.onMouseDown = (event: React.MouseEvent) => {
                    if (interactionTriggeringEvent === ListViewItemInteractionTriggeringEvent.MouseDown || event.button === 0) {
                        renderProps.listView.handleItemPointerInteraction(renderProps.dataItemId, event);
                    }

                    originalOnMouseDown?.(event);
                };
                break;
            case ListViewItemInteractionTriggeringEvent.Click:
            case ListViewItemInteractionTriggeringEvent.LeftButtonClick:
                overridenProps.onClick = (event: React.MouseEvent) => {
                    if (interactionTriggeringEvent === ListViewItemInteractionTriggeringEvent.Click || event.button === 0) {
                        renderProps.listView.handleItemPointerInteraction(renderProps.dataItemId, event);
                    }

                    originalOnClick?.(event);
                };
                break;
        }

        return renderRootElement(tagName, {
            ref: elementRef,
            ...listViewItemRootAttributes,
            className: style(classes.root, {current: renderProps.isCurrent, isSelected: renderProps.isSelected}, className),
            ...overridenProps,
            ...restProps
        });
    }
});

export namespace ListViewItemRoot {

    export interface Props extends AllHTMLAttributes<any>{
        tagName?: string;
        interactionTriggeringEvent?: ListViewItemInteractionTriggeringEvent;
        elementRef?: React.Ref<HTMLElement>;
        renderRootElement?: (tagName: string, attributes: React.HTMLAttributes<any> & {ref?: React.Ref<HTMLElement>}) => React.ReactElement;
    }

}

function defaultRenderRootElement (tagName: string, attributes: React.HTMLAttributes<any> & {ref?: React.Ref<HTMLElement>}) {
    return React.createElement(tagName, attributes);
}

function listViewItemRootRenderRootElementAsButtonComponent (tagName: Required<Button.Props>['tagName'], attributes: React.HTMLAttributes<any> & {ref?: React.Ref<HTMLElement>}) {

    const {
        className,
        ...restAttributes
    } = attributes;

    return (
        <Button
            tagName={tagName}
            {...restAttributes}
            className={style(classes.listViewItemRootButtonRoot, className)}
        />
    )
}

export function listViewItemRootRenderRootElementAsButton (_tagName: string, attributes: React.HTMLAttributes<any> & {ref?: React.Ref<HTMLElement>}) {
    return listViewItemRootRenderRootElementAsButtonComponent('button', attributes);
}

export function listViewItemRootRenderRootElementAsLinkButton (_tagName: string, attributes: React.HTMLAttributes<any> & {ref?: React.Ref<HTMLElement>}) {
    return listViewItemRootRenderRootElementAsButtonComponent('a', attributes);
}

export function listViewItemRootRenderRootElementAsDivButton (_tagName: string, attributes: React.HTMLAttributes<any> & {ref?: React.Ref<HTMLElement>}) {
    return listViewItemRootRenderRootElementAsButtonComponent('div', attributes);
}

type ListViewItemRootHtmlAttributesNames =
    | 'disabled'
    | 'aria-selected'
    | 'aria-disabled'
    | 'role'
    | 'tabIndex'
    | 'onFocus';

type ListViewItemRootAttributes = Partial<Pick<React.AllHTMLAttributes<any>, ListViewItemRootHtmlAttributesNames>>

export function useListViewItemRenderProps () {
    return useContext(ListViewItemContext);
}

export function useListViewItemRootAttributes () {

    const renderItemProps = useContext(ListViewItemContext);

    const {
        isDisabled,
        isSelected,
        tabIndex,
        dataItemId,
        listView,
        listViewId,
        listViewRole = ListViewRole.None,
    } = renderItemProps ?? {};

    return useMemo<ListViewItemRootAttributes>(() => {

        if (listView === undefined || dataItemId === undefined) {
            return {};
        }

        return {
            [ContainingListViewIdAttributeName]: listViewId,
            disabled: isDisabled ? true : undefined,
            'aria-selected': evaluateFunction(() => {
                switch (listViewRole) {
                    case ListViewRole.None:
                    case ListViewRole.List: {
                        return undefined;
                    }
                    case ListViewRole.ListBox:
                    case ListViewRole.TabList:
                    case ListViewRole.Menu: {
                        return isDisabled ? undefined : isSelected;
                    }
                }
            }),
            'aria-disabled': isDisabled ? true : undefined,
            role: evaluateFunction(() => {
                switch (listViewRole) {
                    case ListViewRole.None: return undefined;
                    case ListViewRole.ListBox: return 'option';
                    case ListViewRole.List: return 'listitem';
                    case ListViewRole.TabList: return 'tab';
                    case ListViewRole.Menu: return 'menuitem';
                }
            }),
            tabIndex: tabIndex,
            [ListViewItemIdAttributeName]: JSON.stringify(dataItemId),
            onFocus(event: React.FocusEvent) {
                if (event.target.getAttribute(ContainingListViewIdAttributeName) === listViewId) {
                    listView.updateState(stateController => {
                        stateController.setCurrentItemId(dataItemId);
                    }, StateUpdateReason.PointerInteraction);
                }
            }
        }

    }, [isDisabled, isSelected, tabIndex, dataItemId, listView, listViewId])
}